import { z } from 'zod';

/**
 * O contrato dos pedidos que o `codex app-server` faz AO CLIENTE.
 *
 * Tudo aqui foi lido dos bindings gerados por `codex app-server generate-ts`
 * (711 arquivos, versão 0.154.0). Nada foi imaginado: quando uma forma não
 * aparece nos bindings, o comentário diz de onde ela vem.
 *
 * **Por que este contrato não pode recusar por engano.** Um `ServerRequest`
 * bloqueia a thread do Codex até alguém responder — não existe timeout do lado
 * de lá. Recusar um pedido legítimo por causa de um campo novo não gera erro:
 * gera uma sessão parada para sempre, com `emTurno` preso e `adormecer`
 * recusando eternamente. Por isso a regra deste arquivo é **degradar, nunca
 * lançar**, e por isso o reconhecedor devolve "não atendido" em vez de estourar.
 */

/** `RequestId` do binding: o JSON-RPC permite os dois. */
export const RequestIdSchema = z.union([z.string(), z.number()]);
export type RequestId = z.infer<typeof RequestIdSchema>;

/**
 * Os cinco que o painel sabe desenhar, dos DEZ que o `ServerRequest.ts` lista.
 *
 * Os outros cinco existem e não são atendidos aqui de propósito: `item/tool/call`
 * é ferramenta dinâmica registrada pelo cliente (o nosso MCP é registrado por
 * `config.mcp_servers` e o Codex fala com ele por HTTP, sem passar por aqui),
 * `account/chatgptAuthTokens/refresh` e `attestation/generate` são do login — que
 * é do dono, nunca nosso —, e `applyPatchApproval`/`execCommandApproval` são as
 * versões v1 dos dois primeiros da lista abaixo.
 */
export const METODOS_ATENDIDOS = [
  'item/commandExecution/requestApproval',
  'item/fileChange/requestApproval',
  'item/tool/requestUserInput',
  'mcpServer/elicitation/request',
  'item/permissions/requestApproval',
] as const;
export type MetodoAtendido = (typeof METODOS_ATENDIDOS)[number];

/* Campos que quase todo pedido carrega, escritos uma vez. */
const ALVO = {
  threadId: z.string().min(1),
  turnId: z.string().min(1),
  itemId: z.string().min(1),
  /** Milissegundos do epoch; o binding manda número, não texto ISO. */
  startedAtMs: z.number(),
};

// ───────────────────────────── 1. comando ─────────────────────────────

/**
 * `writeStdin` não é execução de comando: é texto indo para um terminal que já
 * está aberto. O cartão precisa dizer qual dos dois, senão "permitir" aprova
 * uma coisa achando que aprovou outra.
 */
export const CodexTipoDeComandoSchema = z.enum(['command', 'writeStdin']).default('command');

export const CodexComandoParamsSchema = z.object({
  ...ALVO,
  kind: CodexTipoDeComandoSchema,
  /**
   * Nulo na aprovação comum. Vem preenchido quando um mesmo `itemId` gera
   * vários pedidos (subcomando de ponte zsh, stdin) — e aí é ELE que separa um
   * pedido do outro. Responder pelo `itemId` nesses casos responderia ao
   * pedido errado e deixaria o outro pendurado.
   */
  approvalId: z.string().nullable().optional(),
  environmentId: z.string().nullable(),
  reason: z.string().nullable().optional(),
  command: z.string().nullable().optional(),
  cwd: z.string().nullable().optional(),
});
export type CodexComandoParams = z.infer<typeof CodexComandoParamsSchema>;

// ───────────────────────────── 2. edição ──────────────────────────────

/**
 * `move_path` em snake_case NÃO é descuido de digitação: é como o binding
 * escreve, sozinho no meio de um protocolo camelCase. Renomear para `movePath`
 * aqui faria o destino do rename sumir calado — o `z.object` descarta chave
 * desconhecida sem reclamar, e um "arquivo movido" viraria "arquivo movido para
 * lugar nenhum".
 */
export const CodexEspecieDaMudancaSchema = z.union([
  z.object({ type: z.literal('add') }),
  z.object({ type: z.literal('delete') }),
  z.object({ type: z.literal('update'), move_path: z.string().nullable() }),
]);

export const CodexMudancaSchema = z.object({
  path: z.string().min(1),
  kind: CodexEspecieDaMudancaSchema,
  diff: z.string(),
});
export type CodexMudanca = z.infer<typeof CodexMudancaSchema>;

export const CodexEdicaoParamsSchema = z.object({
  ...ALVO,
  reason: z.string().nullable().optional(),
  /** [UNSTABLE] no binding: pedir escrita sob esta raiz pelo resto da sessão. */
  grantRoot: z.string().nullable().optional(),
  /*
    **`changes` NÃO vem no pedido.** Conferido no binding: o
    `FileChangeRequestApprovalParams` tem cinco campos, e nenhum é o diff. O
    conteúdo chega por outro caminho — a notificação
    `FileChangePatchUpdatedNotification {threadId, turnId, itemId, changes}` —,
    e quem casa os dois pelo `itemId` é o índice de itens do daemon.

    Está aqui, opcional, porque é o daemon que enriquece o pedido antes de
    mandá-lo ao painel. Ausente significa "o patch ainda não chegou", e o
    cartão degrada para o pedido sem diff em vez de esperar: o `patchUpdated`
    pode chegar depois do pedido, e segurar o cartão até lá é segurar a thread.
  */
  changes: z.array(CodexMudancaSchema).max(200).optional(),
});
export type CodexEdicaoParams = z.infer<typeof CodexEdicaoParamsSchema>;

// ──────────────────────────── 3. pergunta ─────────────────────────────

export const CodexOpcaoSchema = z.object({
  label: z.string().min(1).max(500),
  descricao: z.string().max(2_000).default(''),
});

export const CodexPerguntaSchema = z.object({
  id: z.string().min(1),
  header: z.string().max(500),
  question: z.string().max(5_000),
  /** Deixa a pessoa escrever a própria resposta além das opções. */
  isOther: z.boolean().default(false),
  /**
   * O que a resposta NÃO pode virar linha de `eventos_sessao`. É append-only e
   * volta pela busca: uma senha digitada aqui ficaria no índice para sempre.
   */
  isSecret: z.boolean().default(false),
  /**
   * `null` no binding, e é pergunta aberta de verdade — não defeito. O painel
   * desenha campo de texto; quem colapsa `null` em `[]` perde a diferença
   * entre "sem opções" e "as opções ainda não vieram".
   */
  options: z.array(CodexOpcaoSchema).max(20).nullable(),
});
export type CodexPergunta = z.infer<typeof CodexPerguntaSchema>;

export const CodexPerguntaParamsSchema = z.object({
  threadId: z.string().min(1),
  turnId: z.string().min(1),
  itemId: z.string().min(1),
  questions: z.array(CodexPerguntaSchema).min(1).max(20),
  /**
   * O binding tem `autoResolutionMs` marcado `@deprecated` dizendo para usar
   * este campo. Não copiamos o depreciado: copiar cria um segundo dono para a
   * mesma decisão, e o dia em que os dois divergirem ninguém saberá qual vale.
   */
  isBlocking: z.boolean().default(true),
});
export type CodexPerguntaParams = z.infer<typeof CodexPerguntaParamsSchema>;

// ─────────────────────────── 4. elicitação ────────────────────────────

const ROTULOS = {
  title: z.string().max(200).optional(),
  description: z.string().max(2_000).optional(),
};

/** `{const, title}`: o valor que volta e o texto que a pessoa lê. */
export const CodexOpcaoTituladaSchema = z.object({
  const: z.string(),
  title: z.string(),
});

/**
 * Escolha única titulada: `oneOf` com rótulo por opção.
 */
export const CodexCampoSelecaoTituladaSchema = z.object({
  type: z.literal('string'),
  ...ROTULOS,
  oneOf: z.array(CodexOpcaoTituladaSchema).min(1).max(50),
  default: z.string().optional(),
});

/**
 * Escolha única simples. Junta as duas formas que o binding separa
 * (`Untitled…` e `LegacyTitled…`): a diferença entre elas é só o `enumNames`
 * opcional, então uma forma com ele opcional aceita as duas sem perder nada.
 */
export const CodexCampoSelecaoSchema = z.object({
  type: z.literal('string'),
  ...ROTULOS,
  enum: z.array(z.string()).min(1).max(50),
  enumNames: z.array(z.string()).max(50).optional(),
  default: z.string().optional(),
});

/** Multi-escolha titulada: `items.anyOf`. */
export const CodexCampoMultiTituladaSchema = z.object({
  type: z.literal('array'),
  ...ROTULOS,
  items: z.object({ anyOf: z.array(CodexOpcaoTituladaSchema).min(1).max(50) }),
  /** `bigint` no binding (u64 do Rust). No fio é número: JSON não leva BigInt. */
  minItems: z.number().int().nonnegative().optional(),
  maxItems: z.number().int().nonnegative().optional(),
  default: z.array(z.string()).max(50).optional(),
});

/** Multi-escolha simples: `items.enum`. */
export const CodexCampoMultiSchema = z.object({
  type: z.literal('array'),
  ...ROTULOS,
  items: z.object({ type: z.literal('string'), enum: z.array(z.string()).min(1).max(50) }),
  minItems: z.number().int().nonnegative().optional(),
  maxItems: z.number().int().nonnegative().optional(),
  default: z.array(z.string()).max(50).optional(),
});

export const CodexCampoTextoSchema = z.object({
  type: z.literal('string'),
  ...ROTULOS,
  minLength: z.number().int().nonnegative().optional(),
  maxLength: z.number().int().nonnegative().optional(),
  format: z.enum(['email', 'uri', 'date', 'date-time']).optional(),
  default: z.string().optional(),
});

export const CodexCampoNumeroSchema = z.object({
  type: z.enum(['number', 'integer']),
  ...ROTULOS,
  minimum: z.number().optional(),
  maximum: z.number().optional(),
  default: z.number().optional(),
});

export const CodexCampoBooleanoSchema = z.object({
  type: z.literal('boolean'),
  ...ROTULOS,
  default: z.boolean().optional(),
});

/**
 * As quatro primitivas do MCP (enum, string, number, boolean), na ORDEM que
 * importa.
 *
 * `z.union` fica com o PRIMEIRO que passar, e `z.object` descarta chave
 * desconhecida em silêncio. Se o texto livre viesse antes, um select
 * (`{type:'string', enum:[…]}`) casaria com ele, o `enum` seria jogado fora, e
 * a pessoa veria uma caixa de texto onde deveria haver uma lista — sem erro em
 * lugar nenhum. Por isso os que têm chave própria (`oneOf`, `enum`, `items`)
 * vêm primeiro, e o texto livre é o último recurso entre os `string`.
 */
export const CodexCampoSchema = z.union([
  CodexCampoSelecaoTituladaSchema,
  CodexCampoSelecaoSchema,
  CodexCampoMultiTituladaSchema,
  CodexCampoMultiSchema,
  CodexCampoTextoSchema,
  CodexCampoNumeroSchema,
  CodexCampoBooleanoSchema,
]);
export type CodexCampo = z.infer<typeof CodexCampoSchema>;

export const CodexFormularioSchema = z.object({
  $schema: z.string().optional(),
  type: z.literal('object'),
  properties: z.record(z.string(), CodexCampoSchema),
  required: z.array(z.string()).max(50).optional(),
});
export type CodexFormulario = z.infer<typeof CodexFormularioSchema>;

const ELICITACAO_BASE = {
  threadId: z.string().min(1),
  /**
   * Nulável, e o binding explica: no MCP a elicitação é um pedido autônomo do
   * servidor, identificado pelo id dele. Correlacionar com um turno é esforço
   * do app-server, não parte do protocolo — então às vezes não há turno.
   */
  turnId: z.string().nullable(),
  serverName: z.string().min(1),
  _meta: z.unknown().nullable().optional(),
  message: z.string().max(10_000),
};

export const CodexElicitacaoParamsSchema = z.discriminatedUnion('mode', [
  z.object({ ...ELICITACAO_BASE, mode: z.literal('form'), requestedSchema: CodexFormularioSchema }),
  /*
    `openai/form` e `openaiForm` são DOIS membros distintos da união, não um
    apelido do outro — estão os dois escritos no binding. E os dois trazem
    `requestedSchema` como JSON solto, sem forma declarada: não há o que tipar,
    e fingir que há seria inventar. Vão para o cartão genérico.
  */
  z.object({
    ...ELICITACAO_BASE,
    mode: z.literal('openai/form'),
    requestedSchema: z.unknown(),
  }),
  z.object({ ...ELICITACAO_BASE, mode: z.literal('openaiForm'), requestedSchema: z.unknown() }),
  z.object({
    ...ELICITACAO_BASE,
    mode: z.literal('url'),
    url: z.string().min(1),
    elicitationId: z.string().min(1),
  }),
]);
export type CodexElicitacaoParams = z.infer<typeof CodexElicitacaoParamsSchema>;

/**
 * Como o painel deve desenhar esta elicitação.
 *
 * `generica` significa "há um formulário, e não sabemos a forma dele" — o
 * cartão mostra o texto e um campo livre. Não é erro nem degradação: é o que
 * os dois modos `openai*` de fato entregam.
 */
export function formaDaElicitacao(p: CodexElicitacaoParams): 'form' | 'generica' | 'url' {
  if (p.mode === 'form') return 'form';
  if (p.mode === 'url') return 'url';
  return 'generica';
}

// ────────────────────────── 5. permissões ─────────────────────────────

/**
 * O perfil pedido. **Os dois campos `null` é a NEGAÇÃO**, não "sem opinião":
 * responder com um perfil vazio é o jeito de dizer "não concedo nada", e é o
 * que o painel manda quando a pessoa recusa.
 */
export const CodexPerfilPedidoSchema = z.object({
  network: z.unknown().nullable(),
  fileSystem: z.unknown().nullable(),
});

export const CodexPermissoesParamsSchema = z.object({
  ...ALVO,
  environmentId: z.string().nullable(),
  cwd: z.string(),
  reason: z.string().nullable(),
  permissions: CodexPerfilPedidoSchema,
});
export type CodexPermissoesParams = z.infer<typeof CodexPermissoesParamsSchema>;

// ─────────────────────────── o reconhecedor ───────────────────────────

export const PedidoCodexSchema = z.discriminatedUnion('method', [
  z.object({
    method: z.literal('item/commandExecution/requestApproval'),
    id: RequestIdSchema,
    params: CodexComandoParamsSchema,
  }),
  z.object({
    method: z.literal('item/fileChange/requestApproval'),
    id: RequestIdSchema,
    params: CodexEdicaoParamsSchema,
  }),
  z.object({
    method: z.literal('item/tool/requestUserInput'),
    id: RequestIdSchema,
    params: CodexPerguntaParamsSchema,
  }),
  z.object({
    method: z.literal('mcpServer/elicitation/request'),
    id: RequestIdSchema,
    params: CodexElicitacaoParamsSchema,
  }),
  z.object({
    method: z.literal('item/permissions/requestApproval'),
    id: RequestIdSchema,
    params: CodexPermissoesParamsSchema,
  }),
]);
export type PedidoCodex = z.infer<typeof PedidoCodexSchema>;

export type ReconhecimentoDePedido =
  | { atendido: true; pedido: PedidoCodex }
  | {
      atendido: false;
      /** `null` quando nem envelope de JSON-RPC havia. */
      metodo: string | null;
      id: RequestId | null;
      motivo: 'sem_envelope' | 'metodo_nao_atendido' | 'params_invalidos';
      detalhe: string;
    };

/** O envelope mínimo, para saber a QUEM responder mesmo sem entender o quê. */
const EnvelopeSchema = z.object({
  method: z.string().min(1),
  id: RequestIdSchema,
});

/**
 * Reconhece um `ServerRequest` sem nunca lançar.
 *
 * Devolver "não atendido" com o `id` em mãos é o ponto inteiro: quem chama
 * PRECISA responder alguma coisa, e um método que não entendemos ainda tem de
 * receber `decline`. A alternativa — deixar a exceção subir — é a thread do
 * Codex esperando para sempre por uma resposta que ninguém vai mandar.
 *
 * Os três motivos não são decoração: `metodo_nao_atendido` é normal e se
 * recusa em silêncio (o app-server tem dez métodos e atendemos cinco);
 * `params_invalidos` é DEFEITO NOSSO ou forma nova do Codex, e merece log alto
 * — mas a resposta sai do mesmo jeito.
 */
export function reconhecerPedidoCodex(bruto: unknown): ReconhecimentoDePedido {
  const envelope = EnvelopeSchema.safeParse(bruto);
  if (!envelope.success) {
    return {
      atendido: false,
      metodo: null,
      id: null,
      motivo: 'sem_envelope',
      detalhe: 'sem `method` ou `id` de JSON-RPC',
    };
  }

  const { method, id } = envelope.data;
  if (!(METODOS_ATENDIDOS as readonly string[]).includes(method)) {
    return { atendido: false, metodo: method, id, motivo: 'metodo_nao_atendido', detalhe: method };
  }

  const analise = PedidoCodexSchema.safeParse(bruto);
  if (!analise.success) {
    return {
      atendido: false,
      metodo: method,
      id,
      motivo: 'params_invalidos',
      detalhe: analise.error.issues
        .slice(0, 3)
        .map((i) => i.path.join('.') + ': ' + i.message)
        .join('; ')
        .slice(0, 300),
    };
  }

  return { atendido: true, pedido: analise.data };
}
